import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Users,
  MoreHorizontal,
  Home,
  List,
  Grid3X3,
  Loader2,
  FileText,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month"); // month, week
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [branding, setBranding] = useState(null);
  
  // New meeting form
  const [newMeeting, setNewMeeting] = useState({
    name: "",
    date: "",
    start_time: "09:00",
    end_time: "10:00",
    venue: "",
    committee_name: "",
  });
  const [creating, setCreating] = useState(false);

  // Drag state
  const [draggedMeeting, setDraggedMeeting] = useState(null);

  useEffect(() => {
    fetchMeetings();
    fetchBranding();
  }, [currentDate]);

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/meetings`);
      setMeetings(response.data || []);
    } catch (error) {
      toast.error("Failed to load meetings");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/company/branding`);
      setBranding(response.data);
    } catch (error) {
      // Use defaults
    }
  };

  const handleCreateMeeting = async () => {
    if (!newMeeting.name.trim() || !newMeeting.date) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/meetings`, newMeeting);
      toast.success("Meeting created");
      setShowCreateModal(false);
      setNewMeeting({
        name: "", date: "", start_time: "09:00", end_time: "10:00",
        venue: "", committee_name: "",
      });
      fetchMeetings();
      // Navigate to the new meeting
      if (response.data?.id) {
        navigate(`/meetings/${response.data.id}`);
      }
    } catch (error) {
      toast.error("Failed to create meeting");
    } finally {
      setCreating(false);
    }
  };

  const handleDateClick = (date) => {
    const dateStr = formatDateForInput(date);
    setSelectedDate(date);
    setNewMeeting({ ...newMeeting, date: dateStr });
    setShowCreateModal(true);
  };

  const handleDragStart = (e, meeting) => {
    setDraggedMeeting(meeting);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, date) => {
    e.preventDefault();
    if (!draggedMeeting) return;
    
    const newDate = formatDateForInput(date);
    if (newDate === draggedMeeting.date) {
      setDraggedMeeting(null);
      return;
    }
    
    try {
      await axios.put(`${API}/meetings/${draggedMeeting.id}`, {
        ...draggedMeeting,
        date: newDate,
      });
      toast.success("Meeting rescheduled");
      fetchMeetings();
    } catch (error) {
      toast.error("Failed to reschedule meeting");
    } finally {
      setDraggedMeeting(null);
    }
  };

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
  
  const formatDateForInput = (date) => {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  const getMeetingsForDate = (date) => {
    const dateStr = formatDateForInput(date);
    return meetings.filter(m => m.date === dateStr);
  };

  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Previous month days
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false,
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }
    
    return days;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const primaryColor = branding?.primary_color || "#0e4f55";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  const calendarDays = generateCalendarDays();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                {branding?.logo_url ? (
                  <img src={branding.logo_url} alt="Logo" className="h-8 w-auto" />
                ) : (
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: primaryColor }}
                  >
                    M
                  </div>
                )}
                <span className="font-semibold text-slate-900 hidden sm:block">MyMeetingsPal</span>
              </div>
              
              <nav className="hidden md:flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/")}
                  className="text-slate-600 hover:text-slate-900"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/")}
                  className="text-slate-600 hover:text-slate-900"
                >
                  <List className="w-4 h-4 mr-2" />
                  List View
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-900 bg-slate-100"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Calendar
                </Button>
              </nav>
            </div>

            <Button
              onClick={() => {
                setNewMeeting({ ...newMeeting, date: formatDateForInput(new Date()) });
                setShowCreateModal(true);
              }}
              className="text-white"
              style={{ backgroundColor: primaryColor }}
              data-testid="create-meeting-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Meeting
            </Button>
          </div>
        </div>
      </header>

      {/* Calendar Controls */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-slate-900">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h1>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={goToPrevMonth}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Today
                </Button>
                <Button variant="outline" size="icon" onClick={goToNextMonth}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "month" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("month")}
                style={viewMode === "month" ? { backgroundColor: primaryColor } : {}}
              >
                <Grid3X3 className="w-4 h-4 mr-2" />
                Month
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-slate-200">
            {DAYS.map((day) => (
              <div
                key={day}
                className="py-3 text-center text-sm font-semibold text-slate-600 bg-slate-50"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {calendarDays.map((dayInfo, idx) => {
              const dayMeetings = getMeetingsForDate(dayInfo.date);
              const isCurrentDay = isToday(dayInfo.date);
              
              return (
                <div
                  key={idx}
                  className={`min-h-[120px] border-b border-r border-slate-100 p-2 cursor-pointer transition-colors ${
                    dayInfo.isCurrentMonth ? "bg-white" : "bg-slate-50/50"
                  } ${draggedMeeting ? "hover:bg-blue-50" : "hover:bg-slate-50"}`}
                  onClick={() => handleDateClick(dayInfo.date)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, dayInfo.date)}
                  data-testid={`calendar-day-${formatDateForInput(dayInfo.date)}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`w-7 h-7 flex items-center justify-center rounded-full text-sm ${
                        isCurrentDay
                          ? "text-white font-semibold"
                          : dayInfo.isCurrentMonth
                          ? "text-slate-900"
                          : "text-slate-400"
                      }`}
                      style={isCurrentDay ? { backgroundColor: primaryColor } : {}}
                    >
                      {dayInfo.date.getDate()}
                    </span>
                    {dayMeetings.length > 0 && (
                      <Badge variant="secondary" className="text-xs px-1.5">
                        {dayMeetings.length}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Meetings */}
                  <div className="space-y-1">
                    {dayMeetings.slice(0, 3).map((meeting) => (
                      <div
                        key={meeting.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, meeting)}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/meetings/${meeting.id}`);
                        }}
                        className="px-2 py-1 rounded text-xs font-medium truncate cursor-move text-white"
                        style={{ backgroundColor: primaryColor }}
                        title={meeting.name}
                      >
                        {meeting.start_time && (
                          <span className="opacity-75 mr-1">{meeting.start_time}</span>
                        )}
                        {meeting.name}
                      </div>
                    ))}
                    {dayMeetings.length > 3 && (
                      <div className="text-xs text-slate-500 pl-2">
                        +{dayMeetings.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend / Info */}
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <p>
            <strong>{meetings.length}</strong> meetings this month •
            Click a date to create a meeting •
            Drag meetings to reschedule
          </p>
        </div>
      </main>

      {/* Create Meeting Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" style={{ color: primaryColor }} />
              Create Meeting
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div>
              <Label>Meeting Name *</Label>
              <Input
                value={newMeeting.name}
                onChange={(e) => setNewMeeting({ ...newMeeting, name: e.target.value })}
                placeholder="e.g., Q1 Board Meeting"
                className="mt-1"
                data-testid="meeting-name-input"
              />
            </div>
            
            <div>
              <Label>Committee / Board</Label>
              <Input
                value={newMeeting.committee_name}
                onChange={(e) => setNewMeeting({ ...newMeeting, committee_name: e.target.value })}
                placeholder="e.g., Board of Directors"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newMeeting.date}
                  onChange={(e) => setNewMeeting({ ...newMeeting, date: e.target.value })}
                  className="mt-1"
                  data-testid="meeting-date-input"
                />
              </div>
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={newMeeting.start_time}
                  onChange={(e) => setNewMeeting({ ...newMeeting, start_time: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={newMeeting.end_time}
                  onChange={(e) => setNewMeeting({ ...newMeeting, end_time: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Venue</Label>
              <Input
                value={newMeeting.venue}
                onChange={(e) => setNewMeeting({ ...newMeeting, venue: e.target.value })}
                placeholder="e.g., Conference Room A"
                className="mt-1"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateMeeting}
                disabled={creating || !newMeeting.name.trim() || !newMeeting.date}
                className="text-white"
                style={{ backgroundColor: primaryColor }}
                data-testid="save-meeting-btn"
              >
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Meeting
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
